import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ApiError } from '@/shared/api/api-error';

import { useAdjustCustomerPoints } from '../api/loyalty.queries';
import { pointsAdjustmentSchema, type PointsAdjustmentFormValues } from '../schemas/loyalty.schema';

const FIELDS = ['direction', 'points', 'reason'] as const;
const REASON_CLASS =
  'border-input bg-background focus-visible:ring-ring w-full rounded-md border px-2 py-1.5 text-xs focus-visible:ring-2 focus-visible:outline-none';

/** One id per dialog: a double submit or a retried request is applied only once by the backend. */
function newRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
    return crypto.randomUUID();
  return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) =>
    (Number(c) ^ (Math.floor(Math.random() * 16) >> (Number(c) / 4))).toString(16),
  );
}

/**
 * Manually add or deduct a customer's points (loyalty.adjust), e.g. to correct a refunded
 * purchase. Two steps: fill in, then confirm a summary. The reason is required and audited;
 * a deduction above the available balance is refused by the backend.
 */
export function PointsAdjustmentDialog({
  customerId,
  customerName,
  available,
  onClose,
}: {
  customerId: number;
  customerName: string;
  available: number;
  onClose: () => void;
}) {
  const [requestId] = useState(newRequestId);
  const [review, setReview] = useState<PointsAdjustmentFormValues | null>(null);
  const adjust = useAdjustCustomerPoints(customerId);
  const form = useForm<PointsAdjustmentFormValues>({
    resolver: zodResolver(pointsAdjustmentSchema),
    defaultValues: { direction: 'add', points: Number.NaN, reason: '' },
  });
  const direction = useWatch({ control: form.control, name: 'direction' });

  const { error } = adjust;
  useEffect(() => {
    if (!(error instanceof ApiError) || !error.fieldErrors) return;
    for (const [field, message] of Object.entries(error.fieldErrors)) {
      const key = FIELDS.find((name) => name.toLowerCase() === field.toLowerCase());
      if (key) form.setError(key, { type: 'server', message });
    }
  }, [error, form]);
  const formError = error instanceof ApiError && !error.fieldErrors ? error.message : null;

  const toReview = (values: PointsAdjustmentFormValues) => {
    if (values.direction === 'deduct' && values.points > available) {
      form.setError('points', {
        type: 'balance',
        message: `Only ${available} points are available; a balance can never go below zero.`,
      });
      return;
    }
    setReview(values);
  };

  const confirm = () =>
    review &&
    adjust.mutate(
      {
        direction: review.direction,
        points: review.points,
        reason: review.reason.trim(),
        requestId,
      },
      {
        onSuccess: onClose,
        // A field error (e.g. the balance changed meanwhile) belongs on the form: go back to it.
        onError: (failure) => {
          if (failure instanceof ApiError && failure.fieldErrors) setReview(null);
        },
      },
    );

  const after = review
    ? available + (review.direction === 'add' ? review.points : -review.points)
    : null;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base">Adjust points</DialogTitle>
          <DialogDescription className="text-xs">
            {customerName} has {available} points available. The change and its reason are recorded
            in the audit log.
          </DialogDescription>
        </DialogHeader>
        {review ? (
          <div className="space-y-4 text-xs">
            <div role="status" className="bg-muted/50 space-y-1 rounded-md border p-3">
              <p className="text-sm font-medium">
                {review.direction === 'add' ? 'Add' : 'Deduct'} {review.points} points
                {review.direction === 'add' ? ' to ' : ' from '}
                {customerName}?
              </p>
              <p className="text-muted-foreground">
                Available: {available} → {after}
              </p>
              <p className="text-muted-foreground">Reason: {review.reason.trim()}</p>
            </div>
            {formError && (
              <p role="alert" className="text-destructive">
                {formError}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setReview(null)}
                disabled={adjust.isPending}
              >
                Back
              </Button>
              <Button
                type="button"
                variant={review.direction === 'deduct' ? 'destructive' : 'default'}
                onClick={confirm}
                disabled={adjust.isPending}
              >
                {adjust.isPending && <Loader2 className="size-3.5 animate-spin" aria-hidden />}
                {review.direction === 'add' ? 'Add points' : 'Deduct points'}
              </Button>
            </div>
          </div>
        ) : (
          <Form {...form}>
            <form noValidate className="space-y-4 text-xs" onSubmit={form.handleSubmit(toReview)}>
              <FormField
                control={form.control}
                name="direction"
                render={({ field }) => (
                  <FormItem>
                    <fieldset className="space-y-1.5">
                      <legend className="mb-1 font-medium">Change</legend>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="points-direction"
                          checked={field.value === 'add'}
                          onChange={() => field.onChange('add')}
                        />
                        Add points
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="points-direction"
                          checked={field.value === 'deduct'}
                          onChange={() => field.onChange('deduct')}
                        />
                        Deduct points
                      </label>
                    </fieldset>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="points"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Points to {direction === 'add' ? 'add' : 'deduct'}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        inputMode="numeric"
                        min={1}
                        step={1}
                        value={Number.isFinite(field.value) ? field.value : ''}
                        onChange={(event) =>
                          field.onChange(
                            event.target.value === '' ? Number.NaN : Number(event.target.value),
                          )
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="reason"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Reason <span className="text-muted-foreground font-normal">(required)</span>
                    </FormLabel>
                    <FormControl>
                      <textarea {...field} rows={2} maxLength={500} className={REASON_CLASS} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit">Review</Button>
              </div>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
